import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import moment from 'moment';
import Utility from '../../../../Utility/Utility';
import { createCustomerBooking } from '../../Booking/Controller/BookingController';
import { useHistory } from 'react-router-dom';

const TourOverviewCard = forwardRef(function TourOverviewCard({
    tourData,
    onPassengerChange,
    initialAdults = 2,
    initialChildren = 0,
    initialInfants = 0,
    customerFormData = {},
    userCredentials = {},
    isGuestFlag = true,
    loaderStateTrue,
    loaderStateFalse,
    onBookingSuccess
}, ref) {
    const history = useHistory();

    // Current active tour (in case user switches departure batch)
    const [selectedTour, setSelectedTour] = useState(tourData);

    useEffect(() => {
        setSelectedTour(tourData);
    }, [tourData]);

    if (!selectedTour || Object.keys(selectedTour).length === 0) {
        return null;
    }

    const {
        id,
        tour_id,
        template_name,
        tour_name,
        tour_type = 'Tour',
        branch_id,
        duration,
        duration_days = 2,
        start_date,
        end_date,
        from_location_name,
        to_location_name,
        tour_scope,
        package_category,
        theme_tags,
        base_currency = 'INR',
        final_selling_price,
        price_for_adult,
        price_for_children,
        minimum_guest_count = 1,
        maximum_guest_count = 100,
        margin_percentage = 0,
        margin_amount = 0,
        offer_amount = 0,
        tax_percentage = 0,
        is_fixed_date,
        allToursList = [],
        Itineraries = [],
        itinerary_json = [],
        Inclusions = [],
        inclusions = [],
        Exclusions = [],
        exclusions = [],
        Checklist = [],
        CustomerPaymentMilestones = [],
        CancellationPolicyData,
        cancellation_policy
    } = selectedTour;

    console.log("kppppppp======", selectedTour);

    // Date state for flexible vs fixed
    const [customStartDate, setCustomStartDate] = useState(start_date || '');

    useEffect(() => {
        if (start_date) {
            setCustomStartDate(start_date || '');
        }
    }, [start_date]);

    // Calculate end date dynamically for flexible tour based on duration_days
    const calculatedEndDate = is_fixed_date
        ? (end_date || '')
        : (customStartDate ? moment(customStartDate).add(Math.max(1, (duration_days || 2) - 1), 'days').format('YYYY-MM-DD') : '');

    // Passenger State
    const [adults, setAdults] = useState(initialAdults);
    const [children, setChildren] = useState(initialChildren);
    const [infants, setInfants] = useState(initialInfants);
    const [childAges, setChildAges] = useState([]);
    const [bookingResult, setBookingResult] = useState(null);
    const [copied, setCopied] = useState(false);
    const [taxApplicability, setTaxApplicability] = useState('CGST_SGST'); // 'NO_TAX', 'CGST_SGST', 'IGST'

    // Tabs for extra details
    const [activeTab, setActiveTab] = useState('itinerary');
    const [expandedDays, setExpandedDays] = useState({ 1: true });

    const tourTitle = template_name || tour_name || 'Tour Details';
    const activeItinerary = (Itineraries && Itineraries.length > 0) ? Itineraries : itinerary_json;
    const activeInclusions = (Inclusions && Inclusions.length > 0) ? Inclusions : inclusions;
    const activeExclusions = (Exclusions && Exclusions.length > 0) ? Exclusions : exclusions;

    // Numerical base rates
    const adultRate = Number(price_for_adult || final_selling_price || 0);
    const childRate = Number(price_for_children || 0);

    // Handlers for passenger counts
    const handleAdultChange = (delta) => {
        setAdults(prev => {
            const next = prev + delta;
            if (next < 1) return 1;
            if (maximum_guest_count && next + children + infants > maximum_guest_count) return prev;
            return next;
        });
    };

    const handleChildChange = (delta) => {
        setChildren(prev => {
            const next = prev + delta;
            if (next < 0) return 0;
            if (maximum_guest_count && adults + next + infants > maximum_guest_count) return prev;

            setChildAges(currAges => {
                if (delta > 0) {
                    return [...currAges, 5];
                } else {
                    return currAges.slice(0, next);
                }
            });
            return next;
        });
    };

    const handleAddChild = () => {
        handleChildChange(1);
    };

    const handleRemoveChildAt = (index) => {
        setChildAges(prev => prev.filter((_, idx) => idx !== index));
        setChildren(prev => Math.max(0, prev - 1));
    };

    const handleChildAgeChange = (index, value) => {
        setChildAges(prev => {
            const updated = [...prev];
            updated[index] = Number(value);
            return updated;
        });
    };

    const handleInfantChange = (delta) => {
        setInfants(prev => {
            const next = prev + delta;
            if (next < 0) return 0;
            return next;
        });
    };

    // Calculate totals
    const totalPax = adults + children + infants;
    const adultsSubtotal = adults * adultRate;
    const childrenSubtotal = children * childRate;
    const subTotal = adultsSubtotal + childrenSubtotal;

    const margin = Number(margin_amount) || (subTotal * (Number(margin_percentage) / 100)) || 0;
    const discount = Number(offer_amount) || 0;

    let cgstRate = 0;
    let sgstRate = 0;
    let igstRate = 0;

    const baseGstRate = Number(tax_percentage) > 0 ? Number(tax_percentage) : 5;

    if (taxApplicability === 'CGST_SGST') {
        cgstRate = baseGstRate / 2;
        sgstRate = baseGstRate / 2;
    } else if (taxApplicability === 'IGST') {
        igstRate = baseGstRate;
    }

    const taxableAmount = Math.max(0, subTotal + margin - discount);
    const cgstAmount = (taxableAmount * cgstRate) / 100;
    const sgstAmount = (taxableAmount * sgstRate) / 100;
    const igstAmount = (taxableAmount * igstRate) / 100;
    const totalTax = cgstAmount + sgstAmount + igstAmount;
    const grandTotal = taxableAmount + totalTax;

    // Minimum advance percentage
    const advanceMilestone = CustomerPaymentMilestones && CustomerPaymentMilestones.length > 0
        ? [...CustomerPaymentMilestones].sort((a, b) => (Number(a.due_days_before_travel) || 0) - (Number(b.due_days_before_travel) || 0))[0]
        : null;
    // console.log("advanceMilestone========", advanceMilestone)
    const minimumPercent = advanceMilestone ? Number(advanceMilestone.percentage) : 25;
    // console.log("minimumPercent========", minimumPercent)


    // Notify parent if callback provided
    useEffect(() => {
        if (typeof onPassengerChange === 'function') {
            onPassengerChange({
                adults,
                children,
                infants,
                childAges,
                taxApplicability,
                adultsSubtotal,
                childrenSubtotal,
                subTotal,
                grandTotal,
                minimumPercent,
                startDate: is_fixed_date ? start_date : customStartDate,
                endDate: calculatedEndDate,
                selectedTour
            });
        }
    }, [adults, children, infants, childAges, taxApplicability, grandTotal, customStartDate, calculatedEndDate, selectedTour]);

    const currencySymbol = base_currency === 'INR' ? '₹' : base_currency;

    const toggleDay = (dayNo) => {
        setExpandedDays(prev => ({
            ...prev,
            [dayNo]: !prev[dayNo]
        }));
    };

    useImperativeHandle(ref, () => ({
        confirmBooking: handleConfirmBookingClick,
        validateBeforePayment: () => {
            if (!(is_fixed_date ? start_date : customStartDate)) {
                Utility.toastNotifications("Please select Departure Date (Start Date)", "Warning", "warning");
                return false;
            }
            return true;
        }
    }));

    // Confirm Booking submission function calling POST api/customer/bookings (called after payment is done)
    const handleConfirmBookingClick = async (paymentList = []) => {
        const finalStartDate = is_fixed_date ? start_date : customStartDate;
        const finalEndDate = is_fixed_date ? end_date : calculatedEndDate;

        if (!finalStartDate) {
            Utility.toastNotifications("Please select Departure Date (Start Date)", "Warning", "warning");
            return;
        }

        const formattedStartDate = (finalStartDate && moment(finalStartDate).isValid())
            ? moment(finalStartDate).format('YYYY-MM-DD')
            : (customStartDate && moment(customStartDate).isValid())
                ? moment(customStartDate).format('YYYY-MM-DD')
                : moment().format('YYYY-MM-DD');

        const formattedEndDate = (finalEndDate && moment(finalEndDate).isValid())
            ? moment(finalEndDate).format('YYYY-MM-DD')
            : (calculatedEndDate && moment(calculatedEndDate).isValid())
                ? moment(calculatedEndDate).format('YYYY-MM-DD')
                : formattedStartDate;

        const formattedValidUntil = (selectedTour?.valid_to && moment(selectedTour.valid_to).isValid())
            ? moment(selectedTour.valid_to).format('YYYY-MM-DD')
            : moment().format('YYYY-MM-DD');

        // Customer details extraction
        let custId = 0;
        let custName = "";
        let custPhone = "";
        let custEmail = "";
        let custGstin = "";
        let compName = "";
        let compAddr = "";

        if (!isGuestFlag && userCredentials && Object.keys(userCredentials).length > 0) {
            custId = userCredentials.id || 0;
            const uDet = userCredentials.user_details || {};
            custName = `${uDet.first_name || ''} ${uDet.last_name || ''}`.trim() || userCredentials.name || '';
            custPhone = uDet.contact_number || userCredentials.contact_number || '';
            custEmail = uDet.user_email || userCredentials.email || '';
        } else if (customerFormData && Object.keys(customerFormData).length > 0) {
            custName = `${customerFormData.name || ''} ${customerFormData.last_name || ''}`.trim();
            custPhone = customerFormData.phone_number || '';
            custEmail = (customerFormData.email && customerFormData.email[0] && customerFormData.email[0].email) || '';
            custGstin = customerFormData.customer_gstin_no || '';
            compName = customerFormData.company_name || '';
            compAddr = customerFormData.company_address || '';
        }

        // Child payload mapping
        const childArr = (childAges || []).map(age => ({
            age: Number(age),
            type: "child"
        }));

        // Cancellation policy normalization
        const finalCancellationPolicy = cancellation_policy || (CancellationPolicyData ? {
            policy_id: CancellationPolicyData.id,
            policy_name: CancellationPolicyData.policy_name,
            description: CancellationPolicyData.description,
            rules: (CancellationPolicyData.Rules || []).map((r, i) => ({
                days_before_travel_from: r.days_before_travel_from,
                days_before_travel_to: r.days_before_travel_to,
                refund_percentage: r.refund_percentage,
                cancellation_charge_percentage: r.cancellation_charge_percentage,
                remarks: r.remarks || "",
                sort_order: i + 1
            }))
        } : null);

        const bookingPayload = [
            {
                org_id: selectedTour.organization_id || null,
                template_type: "Tour",
                payment_arr: (paymentList || []).map(p => ({
                    ...p,
                    amount: String(p.amount ?? ''),
                    comments: p.comments || "",
                    paid_amount: String(p.amount ?? ''),
                    payment_date: moment(p.payment_date).format('YYYY-MM-DD'),
                    payment_mode: p.payment_mode || "cash",
                    payment_ref_id: p.payment_ref_id || "",
                    reference_id: p.payment_ref_id || "",
                    transaction_date: moment(p.payment_date).format('YYYY-MM-DD')
                })),
                template_id: id || tour_id,
                tour_id: tour_id,
                branch_id: branch_id || null,
                calculationType: "quotation",
                customer_id: custId,
                customer_name: custName,
                customer_phone: custPhone,
                customer_email: custEmail,
                customer_gstin_no: custGstin,
                company_name: compName,
                company_address: compAddr,
                hold_inventory: false,
                valid_till_hours: 24,
                email_ids: [],
                adult: adults,
                child: childArr,
                adult_price: adultRate,
                child_price: childRate,
                infant_age_limit: selectedTour.infant_age_limit || null,
                child_age: selectedTour.child_age || null,
                markup_type: selectedTour?.markup_type || "percentage",
                markup_value: Number(selectedTour.markup_value || 0),
                agency_markup_type: selectedTour?.agency_markup_type || "percentage",
                agency_markup_value: Number(selectedTour.agency_markup || 0),
                special_discount_by_percentage: 0,
                special_discount_by_amount: discount,
                no_tax: taxApplicability === 'NO_TAX',
                is_igst_applicable: taxApplicability === 'IGST',
                gst_data: {
                    IGST: taxApplicability === 'IGST' ? igstRate : 0,
                    CGST: taxApplicability === 'CGST_SGST' ? cgstRate : 0,
                    SGST: taxApplicability === 'CGST_SGST' ? sgstRate : 0
                },
                minimum_percent: minimumPercent,
                itinerary_json: activeItinerary,
                inclusions: activeInclusions.map((inc, i) => ({
                    inclusion_text: inc.inclusion_text || inc,
                    sort_order: inc.sort_order || (i + 1),
                    is_active: inc.is_active !== undefined ? inc.is_active : true
                })),
                exclusions: activeExclusions.map((exc, i) => ({
                    exclusion_text: exc.exclusion_text || exc,
                    sort_order: exc.sort_order || (i + 1),
                    is_active: exc.is_active !== undefined ? exc.is_active : true
                })),
                cancellation_policy: finalCancellationPolicy,
                quoted_from: formattedStartDate,
                quoted_to: formattedEndDate,
                lead_id: null,
                template_extracted_data: {},
                valid_untill: formattedValidUntil
            }
        ];

        console.log("bookingPayload=========", bookingPayload)
        // return;
        try {
            if (typeof loaderStateTrue === 'function') loaderStateTrue();
            const response = await createCustomerBooking(bookingPayload);
            if (typeof loaderStateFalse === 'function') loaderStateFalse();

            if (response && (response.success || (Array.isArray(response) && response[0]?.success))) {
                const msg = response.message || (Array.isArray(response) ? response[0]?.message : "Booking Created Successfully");
                Utility.toastNotifications(msg, "Success", "success");
                const resData = Array.isArray(response) ? response[0]?.data : response.data;
                setBookingResult({ booking_number: resData?.booking_number || '', response });
            } else {
                const errMsg = response?.message || (Array.isArray(response) ? response[0]?.message : "Failed to create booking");
                Utility.toastNotifications(errMsg, "Error", "error");
            }
        } catch (error) {
            if (typeof loaderStateFalse === 'function') loaderStateFalse();
            console.error("Booking creation error:", error);
            const errText = error?.response?.data?.message || error?.message || "Booking creation failed";
            Utility.toastNotifications(errText, "Error", "error");
        }
    };

    const handleCopyBookingNumber = async () => {
        try {
            await navigator.clipboard.writeText(bookingResult.booking_number);
        } catch (e) {
            const ta = document.createElement('textarea');
            ta.value = bookingResult.booking_number;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleBookingSuccessClose = () => {
        const res = bookingResult?.response;
        setBookingResult(null);
        if (typeof onBookingSuccess === 'function') {
            onBookingSuccess(res);
        } else {
            history.push(
                Boolean(
                    userCredentials &&
                    Object.keys(userCredentials).length > 0 &&
                    (userCredentials.id || userCredentials.email || userCredentials.customer_id)
                )
                    ? `/${localStorage.getItem('i18nextLng') || 'en'}/booking-history`
                    : `/${localStorage.getItem('i18nextLng') || 'en'}/home`
            );
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100);
        }
    };

    return (
        <div className="tour-overview-wrapper mb-4" style={{ fontFamily: 'inherit' }}>
            {bookingResult && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 20000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="text-center" style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '360px', maxWidth: '92%', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
                        <div style={{ fontSize: '18px', fontWeight: '700', color: '#10b981', marginBottom: '6px' }}>Booking Created Successfully</div>
                        <div className="text-muted" style={{ fontSize: '12px' }}>Your booking number</div>
                        <div className="d-flex align-items-center justify-content-center gap-2 my-3 p-2" style={{ background: '#f1f5f9', borderRadius: '8px' }}>
                            <strong style={{ fontSize: '16px', color: '#1e293b', userSelect: 'all' }}>{bookingResult.booking_number}</strong>
                            <button type="button" className="btn btn-sm btn-outline-primary" title="Copy booking number" onClick={handleCopyBookingNumber}>
                                {copied ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                        <button type="button" className="btn btn-primary w-100" onClick={handleBookingSuccessClose}>
                            View Booking History
                        </button>
                    </div>
                </div>
            )}
            {/* Top Tour Info Header */}
            <div className="tour-mini-header p-3 mb-3" style={{ background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                            {duration && <span className="badge bg-primary text-white" style={{ fontSize: '11px', padding: '4px 8px' }}>⏱️ {duration}</span>}
                            {tour_scope && <span className="badge bg-secondary text-white" style={{ fontSize: '11px', padding: '4px 8px' }}>{tour_scope}</span>}
                            {package_category && <span className="badge bg-info text-dark" style={{ fontSize: '11px', padding: '4px 8px' }}>{package_category}</span>}
                            <span className={`badge ${is_fixed_date ? 'bg-success' : 'bg-warning text-dark'}`} style={{ fontSize: '11px', padding: '4px 8px' }}>
                                {is_fixed_date ? '📅 Fixed Departure' : '✨ Flexible Departure'}
                            </span>
                        </div>
                        <h4 className="mb-1" style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>{tourTitle}</h4>
                        <div className="text-muted" style={{ fontSize: '12px' }}>
                            <span>📍 {from_location_name || 'Origin'}</span>
                            <span className="mx-2">➔</span>
                            <span>🎯 {to_location_name || 'Destination'}</span>
                        </div>
                    </div>
                    <div className="text-end">
                        <span className="text-muted" style={{ fontSize: '11px' }}>Per Adult Rate</span>
                        <div style={{ fontSize: '18px', fontWeight: '700', color: '#10b981' }}>
                            {currencySymbol} {adultRate.toLocaleString()}
                        </div>
                    </div>
                </div>
            </div>

            {/* Departure Dates Selector / Input Section */}
            <div className="card p-3 mb-3" style={{ background: '#f8fafc', borderRadius: '10px', border: '1px solid #cbd5e1' }}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '16px' }}>🗓️</span>
                        <div>
                            <strong style={{ fontSize: '13px', color: '#1e293b' }}>
                                {is_fixed_date ? 'Select Departure Batch (Fixed Date Tour)' : 'Choose Your Travel Date (Flexible Tour)'}
                            </strong>
                            <div className="text-muted" style={{ fontSize: '11px' }}>
                                {is_fixed_date
                                    ? 'This tour operates on fixed departure dates. Select from the available departure schedules below.'
                                    : 'You can customize your departure date. Return date will adjust automatically according to tour duration.'}
                            </div>
                        </div>
                    </div>
                    <span className="badge bg-light text-primary border" style={{ fontSize: '12px' }}>
                        Duration: {duration || `${duration_days} Days`}
                    </span>
                </div>

                {is_fixed_date ? (
                    /* FIXED DATE: Show departure batches options */
                    <div>
                        {allToursList && allToursList.length > 1 ? (
                            <div className="d-flex flex-wrap gap-2 mt-2">
                                {allToursList.map((t, idx) => {
                                    const isSelected = (selectedTour.id === t.id) || (selectedTour.tour_code === t.tour_code);
                                    return (
                                        <button
                                            key={t.id || idx}
                                            type="button"
                                            onClick={() => setSelectedTour({ ...t, allToursList })}
                                            className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            style={{
                                                borderRadius: '8px',
                                                padding: '6px 12px',
                                                fontSize: '12px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            📅 {moment(t.start_date).format('DD MMM YYYY')} – {moment(t.end_date).format('DD MMM YYYY')}
                                            {isSelected && ' ✓'}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            /* Single fixed date batch dropdown / display */
                            <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
                                <div className="bg-white p-2 px-3 border rounded shadow-sm d-flex align-items-center gap-2" style={{ fontSize: '13px' }}>
                                    <span className="text-muted">Departure:</span>
                                    <strong className="text-primary">{moment(start_date).format('DD MMM YYYY')}</strong>
                                    <span className="text-muted mx-1">➔</span>
                                    <span className="text-muted">Return:</span>
                                    <strong className="text-primary">{moment(end_date).format('DD MMM YYYY')}</strong>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    /* FLEXIBLE DATE: User can input departure date */
                    <div className="row g-2 mt-1 align-items-center">
                        <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label mb-1" style={{ fontSize: '11px', fontWeight: '600', color: !customStartDate ? '#dc2626' : '#475569' }}>
                                Departure Date (Start Date) {!customStartDate && <span style={{ color: '#ef4444' }}>* (Select Date)</span>}
                            </label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={customStartDate}
                                min={moment().format('YYYY-MM-DD')}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                style={{
                                    fontSize: '12px',
                                    borderRadius: '6px',
                                    fontWeight: '500',
                                    border: !customStartDate ? '1.5px solid #ef4444' : '1px solid #cbd5e1',
                                    backgroundColor: !customStartDate ? '#fef2f2' : '#ffffff',
                                    boxShadow: !customStartDate ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : 'none',
                                    transition: 'all 0.25s ease-in-out'
                                }}
                            />
                        </div>
                        <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label mb-1" style={{ fontSize: '11px', fontWeight: '600', color: !customStartDate ? '#dc2626' : '#475569' }}>
                                Expected Return Date
                            </label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={calculatedEndDate}
                                disabled
                                style={{
                                    fontSize: '12px',
                                    borderRadius: '6px',
                                    fontWeight: '500',
                                    background: !calculatedEndDate ? '#fef2f2' : '#e2e8f0',
                                    border: !calculatedEndDate ? '1.5px dashed #f87171' : '1px solid #cbd5e1',
                                    boxShadow: !calculatedEndDate ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : 'none',
                                    transition: 'all 0.25s ease-in-out'
                                }}
                            />
                        </div>
                        <div className="col-12 col-md-4">
                            <div className="p-2 border rounded bg-white text-muted mt-md-3" style={{
                                fontSize: '11.5px',
                                background: (!customStartDate || !calculatedEndDate) ? '#fff1f2' : '#ffffff',
                                borderColor: (!customStartDate || !calculatedEndDate) ? '#fda4af' : '#e2e8f0',
                                color: (!customStartDate || !calculatedEndDate) ? '#e11d48' : '#64748b',
                                fontWeight: (!customStartDate || !calculatedEndDate) ? '600' : 'normal'
                            }}>
                                {customStartDate && calculatedEndDate ? `📅 ${moment(customStartDate).format('DD MMM YYYY')} to ${moment(calculatedEndDate).format('DD MMM YYYY')}` : '📅 Please select departure date'}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Passenger Details & Price Summary Section */}
            <div className="row g-3 mb-4">
                {/* 2. Passenger Details */}
                <div className="col-12 col-lg-8">
                    <div className="p-3 h-100" style={{ background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
                        <div className="mb-3">
                            <h5 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '2px' }}>
                                2. Passenger Details
                            </h5>
                            <p className="text-muted mb-0" style={{ fontSize: '12px' }}>
                                Add passengers and their age to get accurate pricing
                            </p>
                        </div>

                        {/* Counter Cards Row */}
                        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                            {/* Adults Box */}
                            <div className="p-2 text-center" style={{ border: '1px solid #e2e8f0', borderRadius: '8px', minWidth: '125px', flex: '1 1 120px' }}>
                                <div style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>Adults (16+ Years)</div>
                                <div className="d-flex align-items-center justify-content-center gap-2 my-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                                        style={{ width: '28px', height: '28px', borderRadius: '6px', padding: 0 }}
                                        onClick={() => handleAdultChange(-1)}
                                    >
                                        −
                                    </button>
                                    <strong style={{ fontSize: '15px', minWidth: '22px' }}>{adults}</strong>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                                        style={{ width: '28px', height: '28px', borderRadius: '6px', padding: 0 }}
                                        onClick={() => handleAdultChange(1)}
                                    >
                                        +
                                    </button>
                                </div>
                                <div style={{ fontSize: '14px', color: '#64748b' }}>👤</div>
                            </div>

                            {/* Children Box */}
                            <div className="p-2 text-center" style={{ border: '1px solid #e2e8f0', borderRadius: '8px', minWidth: '135px', flex: '1 1 120px' }}>
                                <div style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>Children (3 - 15 Years)</div>
                                <div className="d-flex align-items-center justify-content-center gap-2 my-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                                        style={{ width: '28px', height: '28px', borderRadius: '6px', padding: 0 }}
                                        onClick={() => handleChildChange(-1)}
                                    >
                                        −
                                    </button>
                                    <strong style={{ fontSize: '15px', minWidth: '22px' }}>{children}</strong>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                                        style={{ width: '28px', height: '28px', borderRadius: '6px', padding: 0 }}
                                        onClick={() => handleChildChange(1)}
                                    >
                                        +
                                    </button>
                                </div>
                                <div style={{ fontSize: '14px', color: '#64748b' }}>👥</div>
                            </div>

                            {/* Infants Box */}
                            <div className="p-2 text-center" style={{ border: '1px solid #e2e8f0', borderRadius: '8px', minWidth: '125px', flex: '1 1 120px' }}>
                                <div style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>Infants (0-2 Years)</div>
                                <div className="d-flex align-items-center justify-content-center gap-2 my-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                                        style={{ width: '28px', height: '28px', borderRadius: '6px', padding: 0 }}
                                        onClick={() => handleInfantChange(-1)}
                                    >
                                        −
                                    </button>
                                    <strong style={{ fontSize: '15px', minWidth: '22px' }}>{infants}</strong>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                                        style={{ width: '28px', height: '28px', borderRadius: '6px', padding: 0 }}
                                        onClick={() => handleInfantChange(1)}
                                    >
                                        +
                                    </button>
                                </div>
                                <div style={{ fontSize: '14px', color: '#64748b' }}>👶</div>
                            </div>

                            {/* Total Pax Display Box */}
                            <div className="p-2 text-center" style={{ border: '2px solid #3b82f6', background: '#eff6ff', borderRadius: '10px', minWidth: '115px', flex: '1 1 110px' }}>
                                <div style={{ fontSize: '11px', fontWeight: '700', color: '#2563eb', textTransform: 'uppercase' }}>Total Pax</div>
                                <div className="my-1 d-flex align-items-center justify-content-center gap-1" style={{ fontSize: '20px', fontWeight: '800', color: '#1d4ed8' }}>
                                    <span>👥</span> {totalPax}
                                </div>
                                <div style={{ fontSize: '11px', color: '#64748b' }}>
                                    {adults} Adults{children > 0 ? `, ${children} Children` : ''}
                                </div>
                            </div>
                        </div>

                        {/* Child Age Details */}
                        {children > 0 && (
                            <div className="p-2 mb-3" style={{ background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                                <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap">
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>
                                        Child Age Details <small className="text-muted">(Age at the time of travel)</small>
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 text-decoration-none"
                                        style={{ fontSize: '12px', fontWeight: '600' }}
                                        onClick={handleAddChild}
                                    >
                                        + Add Child
                                    </button>
                                </div>
                                <div className="d-flex flex-wrap gap-3 align-items-center">
                                    {childAges.map((age, idx) => (
                                        <div key={idx} className="d-flex align-items-center gap-1 bg-white p-1 px-2 border rounded" style={{ fontSize: '12px' }}>
                                            <span className="text-muted">Child {idx + 1}:</span>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: '90px', fontSize: '12px' }}
                                                value={age}
                                                onChange={(e) => handleChildAgeChange(idx, e.target.value)}
                                            >
                                                {Array.from({ length: 13 }, (_, i) => i + 3).map(yr => (
                                                    <option key={yr} value={yr}>{yr} Years</option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                className="btn btn-sm text-danger p-0 ms-1"
                                                title="Remove child"
                                                onClick={() => handleRemoveChildAt(idx)}
                                                style={{ fontSize: '14px', lineHeight: 1 }}
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Notice */}
                        <div className="p-2 d-flex align-items-center gap-2" style={{ background: '#eff6ff', borderRadius: '6px', fontSize: '12px', color: '#1e40af' }}>
                            <span>ℹ️</span>
                            <span>Pricing will be calculated based on the above passenger mix and selected pricing type.</span>
                        </div>
                    </div>
                </div>

                {/* Price Summary */}
                <div className="col-12 col-lg-4">
                    <div className="p-3 h-100 d-flex flex-column justify-content-between" style={{ background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
                        <div>
                            <div className="d-flex justify-content-between align-items-baseline mb-2">
                                <h5 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: 0 }}>
                                    Price Summary
                                </h5>
                                <span className="text-muted" style={{ fontSize: '10px' }}>(For given passenger mix)</span>
                            </div>

                            {/* Tax Applicability Toggle Buttons */}
                            <div className="mb-3" style={{ display: 'none' }}>
                                <label className="form-label mb-1" style={{ fontSize: '11px', fontWeight: '600', color: !customStartDate ? '#dc2626' : '#475569' }}>
                                    Tax Applicability
                                </label>
                                <div className="btn-group w-100" role="group">
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${taxApplicability === 'NO_TAX' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        style={{ fontSize: '11px', padding: '4px 6px' }}
                                        onClick={() => setTaxApplicability('NO_TAX')}
                                    >
                                        No Tax
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${taxApplicability === 'CGST_SGST' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        style={{ fontSize: '11px', padding: '4px 6px' }}
                                        onClick={() => setTaxApplicability('CGST_SGST')}
                                    >
                                        CGST/SGST
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${taxApplicability === 'IGST' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        style={{ fontSize: '11px', padding: '4px 6px' }}
                                        onClick={() => setTaxApplicability('IGST')}
                                    >
                                        IGST
                                    </button>
                                </div>
                            </div>

                            {/* Line Items */}
                            <div className="price-lines" style={{ fontSize: '12px' }}>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted">
                                        Adults ({adults} × {currencySymbol} {adultRate.toLocaleString()})
                                    </span>
                                    <strong style={{ color: '#0f172a' }}>{currencySymbol} {adultsSubtotal.toLocaleString()}</strong>
                                </div>

                                {children > 0 && (
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">
                                            Children ({children} × {currencySymbol} {childRate.toLocaleString()})
                                        </span>
                                        <strong style={{ color: '#0f172a' }}>{currencySymbol} {childrenSubtotal.toLocaleString()}</strong>
                                    </div>
                                )}

                                {infants > 0 && (
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Infants ({infants})</span>
                                        <span className="text-success fw-bold">Free</span>
                                    </div>
                                )}

                                <hr style={{ margin: '8px 0', borderColor: '#e2e8f0' }} />

                                <div className="d-flex justify-content-between mb-2">
                                    <span style={{ fontWeight: '600' }}>Sub Total</span>
                                    <strong style={{ color: '#0f172a' }}>{currencySymbol} {subTotal.toLocaleString()}</strong>
                                </div>

                                {margin > 0 && (
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Margin ({margin_percentage || 0}%)</span>
                                        <span>{currencySymbol} {margin.toLocaleString()}</span>
                                    </div>
                                )}

                                {discount > 0 && (
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Discount (Flat)</span>
                                        <span className="text-danger">- {currencySymbol} {discount.toLocaleString()}</span>
                                    </div>
                                )}

                                {taxApplicability === 'CGST_SGST' && (
                                    <>
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-muted">CGST ({cgstRate}%)</span>
                                            <span>{currencySymbol} {cgstAmount.toFixed(2)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">SGST ({sgstRate}%)</span>
                                            <span>{currencySymbol} {sgstAmount.toFixed(2)}</span>
                                        </div>
                                    </>
                                )}

                                {taxApplicability === 'IGST' && (
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">IGST ({igstRate}%)</span>
                                        <span>{currencySymbol} {igstAmount.toFixed(2)}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Grand Total & Confirm Booking Button */}
                        <div className="pt-2 mt-2" style={{ borderTop: '2px solid #e2e8f0' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span style={{ fontSize: '15px', fontWeight: '700', color: '#1e40af' }}>Grand Total</span>
                                <span style={{ fontSize: '20px', fontWeight: '800', color: '#2563eb' }}>
                                    {currencySymbol} {Math.round(grandTotal).toLocaleString()}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs for Itinerary, Inclusions, Milestones */}
            <ul className="nav nav-pills mb-3" style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', gap: '8px' }}>
                <li className="nav-item">
                    <button
                        type="button"
                        className={`btn btn-sm ${activeTab === 'itinerary' ? 'btn-primary' : 'btn-light'}`}
                        style={{ borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}
                        onClick={() => setActiveTab('itinerary')}
                    >
                        📋 Day-wise Itinerary ({activeItinerary.length})
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        type="button"
                        className={`btn btn-sm ${activeTab === 'inclusions' ? 'btn-primary' : 'btn-light'}`}
                        style={{ borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}
                        onClick={() => setActiveTab('inclusions')}
                    >
                        ✅ Inclusions & Exclusions
                    </button>
                </li>
                {CustomerPaymentMilestones.length > 0 && (
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`btn btn-sm ${activeTab === 'milestones' ? 'btn-primary' : 'btn-light'}`}
                            style={{ borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}
                            onClick={() => setActiveTab('milestones')}
                        >
                            💳 Payment Milestones
                        </button>
                    </li>
                )}
                {Checklist.length > 0 && (
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`btn btn-sm ${activeTab === 'checklist' ? 'btn-primary' : 'btn-light'}`}
                            style={{ borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}
                            onClick={() => setActiveTab('checklist')}
                        >
                            📑 Required Checklist
                        </button>
                    </li>
                )}
            </ul>

            {/* Tab Contents */}
            <div className="tab-body">
                {/* 1. ITINERARY TAB */}
                {activeTab === 'itinerary' && (
                    <div className="itinerary-list">
                        {activeItinerary.length === 0 ? (
                            <p className="text-muted" style={{ fontSize: '13px' }}>No itinerary details provided.</p>
                        ) : (
                            activeItinerary.map((day, idx) => {
                                const dayNo = day.day_number || (idx + 1);
                                const isExpanded = !!expandedDays[dayNo];
                                return (
                                    <div
                                        key={dayNo || idx}
                                        className="day-accordion mb-2"
                                        style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}
                                    >
                                        <div
                                            className="day-header d-flex justify-content-between align-items-center p-2 px-3"
                                            style={{ background: '#f8fafc', cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => toggleDay(dayNo)}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="badge bg-dark text-white" style={{ fontSize: '11px' }}>
                                                    Day {dayNo}
                                                </span>
                                                <strong style={{ fontSize: '13px', color: '#1f2937' }}>{day.title}</strong>
                                            </div>
                                            <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                                {isExpanded ? '▲ Hide' : '▼ Show'}
                                            </span>
                                        </div>

                                        {isExpanded && (
                                            <div className="day-content p-3" style={{ background: '#fff' }}>
                                                {day.description && (
                                                    <p className="text-muted mb-3" style={{ fontSize: '12.5px' }}>{day.description}</p>
                                                )}
                                                {day.items && day.items.length > 0 && (
                                                    <div className="day-items-timeline" style={{ borderLeft: '2px solid #e2e8f0', marginLeft: '8px', paddingLeft: '15px' }}>
                                                        {day.items.map((item, itemIdx) => (
                                                            <div key={item.id || itemIdx} className="timeline-item mb-3 relative" style={{ position: 'relative' }}>
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    left: '-21px',
                                                                    top: '4px',
                                                                    width: '10px',
                                                                    height: '10px',
                                                                    borderRadius: '50%',
                                                                    background: '#3b82f6'
                                                                }}></div>
                                                                <div className="d-flex justify-content-between align-items-baseline flex-wrap">
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <strong style={{ fontSize: '13px', color: '#111827' }}>{item.title}</strong>
                                                                        {item.type && (
                                                                            <span className="badge bg-light text-primary border" style={{ fontSize: '10px' }}>
                                                                                {item.type}
                                                                            </span>
                                                                        )}
                                                                        {item.duration && (
                                                                            <span className="text-muted" style={{ fontSize: '11px' }}>⏱️ {item.duration}</span>
                                                                        )}
                                                                    </div>
                                                                    {item.price && Number(item.price) > 0 ? (
                                                                        <span className="text-success" style={{ fontSize: '12px', fontWeight: '600' }}>
                                                                            {currencySymbol} {item.price}
                                                                        </span>
                                                                    ) : null}
                                                                </div>
                                                                {item.description && (
                                                                    <div className="text-muted mt-1" style={{ fontSize: '12px' }}>{item.description}</div>
                                                                )}
                                                                {item.location && (
                                                                    <div className="text-secondary mt-1" style={{ fontSize: '11px' }}>📍 {item.location}</div>
                                                                )}
                                                                {item.notes && (
                                                                    <div className="text-warning mt-1" style={{ fontSize: '11px', fontStyle: 'italic' }}>ℹ️ {item.notes}</div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                {/* 2. INCLUSIONS & EXCLUSIONS TAB */}
                {activeTab === 'inclusions' && (
                    <div className="row g-3">
                        <div className="col-12 col-md-6">
                            <div className="p-3" style={{ background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', height: '100%' }}>
                                <h6 style={{ color: '#166534', fontWeight: '700', fontSize: '13px', marginBottom: '10px' }}>
                                    ✅ What's Included
                                </h6>
                                {activeInclusions.length === 0 ? (
                                    <div className="text-muted" style={{ fontSize: '12px' }}>No inclusions specified.</div>
                                ) : (
                                    <ul className="list-unstyled mb-0" style={{ fontSize: '12px' }}>
                                        {activeInclusions.map((inc, i) => (
                                            <li key={inc.id || i} className="mb-2 d-flex align-items-start gap-2">
                                                <span style={{ color: '#15803d' }}>✔</span>
                                                <span style={{ color: '#14532d' }}>{inc.inclusion_text || inc}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        <div className="col-12 col-md-6">
                            <div className="p-3" style={{ background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', height: '100%' }}>
                                <h6 style={{ color: '#991b1b', fontWeight: '700', fontSize: '13px', marginBottom: '10px' }}>
                                    ❌ What's Not Included
                                </h6>
                                {activeExclusions.length === 0 ? (
                                    <div className="text-muted" style={{ fontSize: '12px' }}>No exclusions specified.</div>
                                ) : (
                                    <ul className="list-unstyled mb-0" style={{ fontSize: '12px' }}>
                                        {activeExclusions.map((exc, i) => (
                                            <li key={exc.id || i} className="mb-2 d-flex align-items-start gap-2">
                                                <span style={{ color: '#dc2626' }}>✖</span>
                                                <span style={{ color: '#7f1d1d' }}>{exc.exclusion_text || exc}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* 3. PAYMENT MILESTONES TAB */}
                {activeTab === 'milestones' && (
                    <div className="table-responsive">
                        <table className="table table-bordered table-sm mb-0" style={{ fontSize: '12px' }}>
                            <thead className="table-light">
                                <tr>
                                    <th>Milestone</th>
                                    <th>Percentage</th>
                                    <th>Timeline</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {CustomerPaymentMilestones.map((m, idx) => (
                                    <tr key={m.id || idx}>
                                        <td className="fw-bold">{m.milestone_name}</td>
                                        <td>{m.percentage}%</td>
                                        <td>{m.due_days_before_travel ? `${m.due_days_before_travel} days before travel` : 'On booking / completion'}</td>
                                        <td className="text-muted">{m.description || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* 4. CHECKLIST TAB */}
                {activeTab === 'checklist' && (
                    <div className="checklist-items">
                        <ul className="list-group list-group-flush" style={{ fontSize: '12px' }}>
                            {Checklist.map((c, idx) => (
                                <li key={c.id || idx} className="list-group-item d-flex justify-content-between align-items-center px-0">
                                    <div>
                                        <strong className="text-dark">{c.document_name}</strong>
                                        {c.description && <div className="text-muted">{c.description}</div>}
                                    </div>
                                    {c.is_mandatory && (
                                        <span className="badge bg-danger text-white" style={{ fontSize: '10px' }}>Mandatory</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
});

export default TourOverviewCard;
